import { useEffect, useRef, useState } from "react";

// cursor 로그를 한 번 읽은 결과. 특정 API DTO를 import하지 않는다. 두 endpoint가 공유하는
// 최소 구조만 요구한다.
export type CursorLogPage = {
  lines: readonly string[];
  next_cursor: string | null;
  eof: boolean;
};

export type CursorLogsQuery = { since?: string; limit: number };
export type CursorLogsFetcher = (query: CursorLogsQuery) => Promise<CursorLogPage>;

export type CursorLogsErrorDecision = {
  message: string;
  terminal: boolean;
};

export type CursorLogsOptions = {
  targetActive: boolean;
  // HTTP 종류와 사용자 문구는 소비자가 정한다. lifecycle은 재시도 여부만 적용한다.
  resolveError: (error: unknown) => CursorLogsErrorDecision;
};

export type CursorLogsPhase = "loading" | "polling" | "eof" | "error";

// 일반 LoadState와 다르게 로그 버퍼는 일시 오류가 나도 유지된다. eof는 별도 종단 상태다.
export type CursorLogsState = {
  lines: string[];
  phase: CursorLogsPhase;
  lastError: string | null;
};

// 로그 cursor 계약의 상한 주기. 요청이 끝난 뒤 다음 tick을 예약한다.
// 그래서 in-flight 요청이 겹치지 않는다.
export const cursorLogsPollMs = 3000;
const cursorLogsPageLimit = 500;
export const maxCursorLogLines = 5000;
const idleTicksBeforeStop = 3;

function trimBuffer(lines: readonly string[]): string[] {
  return lines.length > maxCursorLogLines ? lines.slice(-maxCursorLogLines) : Array.from(lines);
}

/**
 * cursor 기반 로그를 직렬 polling하는 공용 수명주기 hook.
 *
 * endpoint·HTTP 오류 해석은 소비자가 주입한다. 이 hook은 cursor 전진, tail 교체, 버퍼 상한,
 * eof·idle 중단, unmount 정리만 소유한다. `fetchPage`가 바뀌면 새 대상으로 보고 초기화한다.
 */
export function useCursorLogs(
  fetchPage: CursorLogsFetcher,
  options: CursorLogsOptions,
): CursorLogsState {
  const [lines, setLines] = useState<string[]>([]);
  const [phase, setPhase] = useState<CursorLogsPhase>("loading");
  const [lastError, setLastError] = useState<string | null>(null);
  const targetActiveRef = useRef(options.targetActive);
  const resolveErrorRef = useRef(options.resolveError);
  targetActiveRef.current = options.targetActive;
  resolveErrorRef.current = options.resolveError;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cursor: string | null = null;
    let idleTicks = 0;

    setLines([]);
    setPhase("loading");
    setLastError(null);

    async function tick(): Promise<void> {
      try {
        const page = await fetchPage({
          since: cursor ?? undefined,
          limit: cursorLogsPageLimit,
        });
        if (cancelled) return;
        const advanced = page.next_cursor !== null && page.next_cursor !== cursor;
        if (cursor === null || !advanced) {
          // 첫 tail, cursor 없는 소스, cursor가 전진하지 않은 echo는 snapshot이다.
          // 빈 echo만 기존 버퍼를 유지한다.
          if (page.lines.length > 0 || cursor === null) {
            setLines(trimBuffer(page.lines));
          }
        } else {
          setLines((previous) => trimBuffer([...previous, ...page.lines]));
        }
        cursor = page.next_cursor ?? cursor;
        setLastError(null);
        if (page.eof) {
          setPhase("eof");
          return;
        }
        const progressed = page.lines.length > 0 || advanced;
        idleTicks = progressed ? 0 : idleTicks + 1;
        if (!targetActiveRef.current && idleTicks >= idleTicksBeforeStop) {
          setPhase("eof");
          return;
        }
        setPhase("polling");
      } catch (error: unknown) {
        if (cancelled) return;
        const decision = resolveErrorRef.current(error);
        setLastError(decision.message);
        if (decision.terminal) {
          setPhase("error");
          return;
        }
        // 일시 오류는 버퍼를 유지하고 다음 tick에서 재시도한다.
      }
      timer = setTimeout(() => void tick(), cursorLogsPollMs);
    }

    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [fetchPage]);

  return { lines, phase, lastError };
}
